import { useNavigate } from "react-router-dom"
import { ArrowLeft, Users, Eye, Target, Clock, AlertTriangle, Lightbulb, CheckCircle, XCircle } from "lucide-react"
import { Button } from "./ui/button"
import { Footer } from "./layout/Footer"
import SEO from "../utils/SEO"

export default function HowToPlayPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <SEO 
        title="How To Play Spyfall"
        description="Learn how to play Spyfall with our comprehensive guide. Master the art of deception and deduction whether you're the spy or a regular player."
        keywords="spyfall rules, how to play spyfall, spyfall game guide, spy game rules, deduction game"
      />
      
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-gray-800 bg-black/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => window.location.replace('/')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-xl font-bold text-red-500">SPYFALL</span>
            </button>
          </div>
          <Button 
            variant="outline" 
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            onClick={() => navigate('/app')}
          >
            Start Playing
          </Button>
        </div>
      </header>

      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h1 className="text-4xl font-bold mb-4">How To Play Spyfall</h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Master the art of deception and deduction with our comprehensive guide to Spyfall. Whether you're a
              regular player trying to catch the spy or the spy trying to blend in, these instructions will help you
              succeed in your mission.
            </p>
          </div>

          {/* Game Overview */}
          <section className="mb-16">
            <div className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Users className="h-6 w-6 text-red-500" />
                Game Overview
              </h2>

              <div className="space-y-4">
                <p>
                  Spyfall is a party game of deception and deduction. In each round, players are secretly assigned
                  roles: one player is the spy, and all other players know the location but don't know who the spy is.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2 text-red-400">Game Setup</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>3-10 players - 1 Player is the Spy</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          Each player receives a card showing either the location or identifying them as the spy
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>3-15-minute timer for each round, the more players you have increase the timer</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2 text-red-400">Winning Conditions</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          <strong>Regular players win</strong> if they correctly identify the spy <strong>(1 point)</strong>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          <strong>The spy wins</strong> if they avoid detection they get <strong>(2 points)</strong> or correctly guess the location <strong>(1 point)</strong>
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>If an innocent player is incorrectly accused of being the spy, the spy escapes and wins <strong>(2 points)</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Replace image placeholder with a styled box */}
                <div className="mt-6 grid grid-cols-1 gap-6">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2 text-red-400">During the Round</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          Players take turns asking one another questions about the location, anyone can start the round.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                            All players are free to question any other player, with the exception that a player cannot be questioned immediately after asking a question.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                           You can only ask one question at a time, no follow up questions, after asking the question. e.g "oh really, tell me more?"
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          The spy must listen carefully and attempt to figure out the location
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          At any time, a player can accuse someone of being the spy - requires a majority vote - if the majority vote is incorrect the spy wins the round.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          The spy can reveal themselves at anytime and guess the location! They win the round and get 2 points.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          When the timer ends, the spy wins the round and gets 2 points. (Always keep an eye on the timer.)
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <span>
                          Play as many rounds as you want, the spy & location is shuffled among the players each round.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Playing as a Regular Player */}
          <section className="mb-16">
            <div className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <CheckCircle className="h-6 w-6 text-red-500" />
                Playing as a Regular Player
              </h2>

              <div className="space-y-4">
                <p>
                  As a regular player, your mission is to identify the spy without revealing the location to them. This
                  requires careful questioning and observation of other players' responses.
                </p>

                <div className="mt-6 space-y-6">
                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold mb-3 text-red-400">Your Objectives</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          1
                        </div>
                        <div>
                          <p className="font-medium">Identify the spy without giving away the location</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Ask questions and analyze responses to determine who seems unsure about the location.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          2
                        </div>
                        <div>
                          <p className="font-medium">Answer questions carefully</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Provide answers that show you know the location without being too obvious for the spy.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          3
                        </div>
                        <div>
                          <p className="font-medium">Collaborate with other regular players</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Pay attention to how others answer questions to build consensus on who might be the spy.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          4
                        </div>
                        <div>
                          <p className="font-medium">Call for a vote when you're confident</p>
                          <p className="text-sm text-gray-400 mt-1">
                            When you believe you know who the spy is, suggest a vote to the group.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Image placeholder for regular player strategy */}
                  <div className="relative h-64 bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
                    <div className="text-gray-500 flex flex-col items-center">
                      <img
                        src="/imgs/howtoimgs/locationscreen2.webp?height=500&width=800"
                        alt="Players asking questions during a game"
                        className="object-cover h-auto w-full"
                      />
                      <p className="absolute bottom-2 text-xs text-gray-500">Regular players questioning each other</p>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold mb-3 text-red-400">Question Strategies</h3>
                    <p className="mb-4 text-sm text-gray-300">
                      The art of asking questions is crucial. Here are some effective approaches:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">Indirect Questions</p>
                        <p className="text-sm mt-1">
                          "How did you get to this place today?" instead of "What vehicles are at this location?"
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">Role-Based Questions</p>
                        <p className="text-sm mt-1">
                          "What's your favorite part of working here?" assumes knowledge of the location.
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">Verification Questions</p>
                        <p className="text-sm mt-1">
                          Ask about specific details that someone familiar with the location would know.
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">Reaction Questions</p>
                        <p className="text-sm mt-1">
                          Watch how quickly and confidently players answer, not just what they say.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Playing as the Spy */}
          <section className="mb-16">
            <div className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Eye className="h-6 w-6 text-red-500" />
                Playing as the Spy
              </h2>

              <div className="space-y-4">
                <p>
                  As the spy, your mission is to blend in with the other players while trying to figure out the
                  location. You'll need to be observant, quick-thinking, and maintain your cover at all costs.
                </p>

                <div className="mt-6 space-y-6">
                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold mb-3 text-red-400">Your Objectives</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          1
                        </div>
                        <div>
                          <p className="font-medium">Avoid detection</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Blend in by giving vague but plausible answers that don't reveal your ignorance of the
                            location.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          2
                        </div>
                        <div>
                          <p className="font-medium">Gather information</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Listen carefully to questions and answers to deduce the possible location.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          3
                        </div>
                        <div>
                          <p className="font-medium">Ask revealing questions</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Craft questions that will give you clues about the location without exposing yourself.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-500 font-bold text-sm">
                          4
                        </div>
                        <div>
                          <p className="font-medium">Identify the location or survive the round</p>
                          <p className="text-sm text-gray-400 mt-1">
                            If you're confident about the location, you can reveal yourself and guess. Otherwise, try to
                            avoid suspicion until time runs out.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Image placeholder for spy strategy */}
                  <div className="relative h-64 bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
                    <div className="text-gray-500 flex flex-col items-center">
                      <img
                        src="/imgs/howtoimgs/spyScreen.webp?height=400&width=800"
                        alt="A spy trying to blend in with other players"
                        className="object-cover h-auto w-full"
                      />
                      <p className="absolute bottom-2 text-xs text-gray-500">
                        The spy carefully observing other players
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-xl font-semibold mb-3 text-red-400">Spy Survival Tactics</h3>

                    <div className="space-y-4">
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">The Echo Technique</p>
                        <p className="text-sm mt-1">
                          Rephrase information you've heard from others to craft your answers, making them seem
                          knowledgeable.
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">The Deflection Strategy</p>
                        <p className="text-sm mt-1">
                          When asked a difficult question, respond with another question or a vague answer that could
                          apply to many locations.
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">The Confident Bluff</p>
                        <p className="text-sm mt-1">
                          Sometimes, a confident wrong answer is better than a hesitant correct one. Speak with
                          authority even when guessing.
                        </p>
                      </div>

                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="font-medium text-red-300">The Accusation Diversion</p>
                        <p className="text-sm mt-1">
                          If you're under suspicion, confidently accuse someone else of being the spy to shift attention
                          away from yourself.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Advanced Tips and Tricks */}
          <section className="mb-16">
            <div className="bg-gray-900 rounded-lg p-6 border-l-4 border-red-500">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Lightbulb className="h-6 w-6 text-red-500" />
                Advanced Tips and Tricks
              </h2>

              <div className="space-y-6">
                <p>
                  Master the game with these advanced strategies that will elevate your Spyfall experience, whether
                  you're playing as a regular player or as the spy.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold mb-3 text-red-400 flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      For All Players
                    </h3>

                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <div>
                          <p className="font-medium">Body Language Matters</p>
                          <p className="text-xs text-gray-400">
                            Watch for physical tells like hesitation, nervousness, or excessive confidence.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <div>
                          <p className="font-medium">Question Order Strategy</p>
                          <p className="text-xs text-gray-400">
                            The order in which you ask players questions can reveal patterns and inconsistencies.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <div>
                          <p className="font-medium">Timing is Everything</p>
                          <p className="text-xs text-gray-400">
                            Know when to call for a vote or when to reveal yourself as the spy.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-500 font-bold mt-0.5">•</span>
                        <div>
                          <p className="font-medium">Cross-Round Awareness</p>
                          <p className="text-xs text-gray-400">
                            Be aware of players' typical behaviors across multiple rounds to spot deviations.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-800 p-5 rounded-lg">
                    <h3 className="text-lg font-semibold mb-3 text-red-400 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Common Mistakes to Avoid
                    </h3>

                    <ul className="space-y-3">
                      <li className="flex items-start gap-2">
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Being Too Specific</p>
                          <p className="text-xs text-gray-400">
                            Regular players should avoid questions that directly reveal the location.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Staying Silent</p>
                          <p className="text-xs text-gray-400">
                            Not participating makes you suspicious. Everyone should engage actively.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Rushing Accusations</p>
                          <p className="text-xs text-gray-400">
                            Making hasty accusations without sufficient evidence often backfires.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Forgetting the Clock</p>
                          <p className="text-xs text-gray-400">
                            Time management is crucial. Don't let the 8 minutes expire without a plan.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Image placeholder for advanced strategies */}
                

                <div className="bg-gray-800 p-5 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-red-400 flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Game Variations
                  </h3>

                  <div className="space-y-3">
                    <div className="bg-gray-700/50 p-3 rounded">
                      <p className="font-medium text-red-300">Double Agent Mode</p>
                      <p className="text-sm mt-1">
                        Add a second spy who knows they're a spy but doesn't know who the other spy is.
                      </p>
                    </div>

                    <div className="bg-gray-700/50 p-3 rounded">
                      <p className="font-medium text-red-300">Time Pressure</p>
                      <p className="text-sm mt-1">Reduce the timer to 6 minutes for a more intense experience.</p>
                    </div>

                    <div className="bg-gray-700/50 p-3 rounded">
                      <p className="font-medium text-red-300">Location Hints</p>
                      <p className="text-sm mt-1">
                        Give the spy a small hint about the location category (indoor, outdoor, public, private).
                      </p>
                    </div>

                    <div className="bg-gray-700/50 p-3 rounded">
                      <p className="font-medium text-red-300">Role Assignment</p>
                      <p className="text-sm mt-1">
                        Assign specific roles to regular players within the location (e.g., at a hospital: doctor,
                        nurse, patient).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Ready to Play */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-red-900 to-red-700 rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-4">Ready to Test Your Skills?</h2>
              <p className="mb-6 max-w-xl mx-auto">
                Now that you know the rules and strategies, it's time to put your spy skills to the test. Remember,
                practice makes perfect – the more you play, the better you'll become at both roles!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-black hover:bg-gray-900"
                  onClick={() => navigate('/app')}
                >
                  Start a New Game
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-black text-black hover:bg-black hover:text-white"
                  onClick={() => window.location.replace('/')}
                >
                  Back to Home
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

